<template>
  <div class="financing-section">
    <div
      class="financing-section__header"
      :class="{ 'financing-section__header--open': isOpen }"
      @click="toggle"
    >
      <span class="financing-section__title">
        {{ title }}<span v-if="required" class="financing-section__required"> *</span>
      </span>
      <span class="financing-section__icon">{{ isOpen ? '▲' : '▼' }}</span>
    </div>

    <div v-show="isOpen" class="financing-section__body">
      <quill-editor
        :ref="'quillEditor_' + sectionKey"
        v-model="localContent"
        :options="editorOption"
        :disabled="disabled"
        @change="onEditorChange($event)"
      />

      <div class="financing-section__attachments">
        <p class="financing-section__attachments-label">Anexo desta seção</p>

        <div v-if="existingAttachment && !pendingNewFile" class="financing-section__existing-attachment">
          <a :href="existingAttachment.url" target="_blank" :title="existingAttachment.name" class="financing-section__attachment-link">
            <i class="tim-icons icon-cloud-download-93"></i>
            <span>{{ existingAttachment.name }}</span>
          </a>
          <a
            v-if="!disabled"
            href="#"
            class="financing-section__attachment-remove"
            title="Remover anexo"
            @click.prevent="removeExistingAttachment"
          >
            <i class="tim-icons icon-trash-simple"></i>
          </a>
        </div>

        <VueFileAgent
          v-if="!disabled && !existingAttachment"
          :ref="'fileAgent_' + sectionKey"
          v-model="fileRecords"
          :multiple="false"
          :deletable="true"
          :meta="false"
          :accept="'image/*,.pdf,.doc,.docx,.xls,.xlsx'"
          :maxSize="'10MB'"
          :helpText="'Clique para selecionar um arquivo'"
          :errorText="{
            type: 'Tipo de arquivo não suportado',
            size: 'O arquivo não pode ter mais de 10MB',
          }"
          @beforedelete="onBeforeDelete($event)"
          @delete="onDelete($event)"
        ></VueFileAgent>
      </div>
    </div>
  </div>
</template>

<script>
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';

import { quillEditor } from 'vue-quill-editor';

export default {
  name: 'FinancingSection',

  components: {
    quillEditor,
  },

  props: {
    title: {
      type: String,
      required: true,
    },
    sectionKey: {
      type: String,
      required: true,
    },
    value: {
      type: Object,
      default: () => ({ content: '', attachment: null }),
    },
    startOpen: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isOpen: this.startOpen,
      localContent: (this.value && this.value.content) || '',
      fileRecords: [],
      existingAttachment: (this.value && this.value.attachment) || null,
      attachmentDeleted: false,
      deletedAttachmentPath: null,
      editorOption: {
        placeholder: 'Descreva o conteúdo desta seção...',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ header: [1, 2, 3, false] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }],
            ['link', 'image', 'video'],
            ['clean'],
          ],
        },
      },
    };
  },

  computed: {
    pendingNewFile() {
      return this.fileRecords.length > 0 && !this.fileRecords[0].error;
    },
  },

  watch: {
    value: {
      deep: true,
      handler(newVal) {
        if (newVal && newVal.content !== this.localContent) {
          this.localContent = newVal.content || '';
        }
        if (newVal && newVal.attachment !== this.existingAttachment) {
          this.existingAttachment = newVal.attachment || null;
        }
      },
    },
  },

  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
    },

    onEditorChange({ html }) {
      this.localContent = html;
      this.$emit('input', {
        content: html,
        attachment: this.existingAttachment,
      });
    },

    removeExistingAttachment() {
      this.attachmentDeleted = true;
      this.deletedAttachmentPath = this.existingAttachment && this.existingAttachment.path;
      this.existingAttachment = null;
      this.$emit('input', {
        content: this.localContent,
        attachment: null,
      });
    },

    onBeforeDelete(fileRecord) {
      const confirmed = confirm('Deseja remover este arquivo?');
      if (confirmed) {
        this.$refs['fileAgent_' + this.sectionKey].deleteFileRecord(fileRecord);
      }
    },

    onDelete() {
      this.fileRecords = [];
    },

    getNewFile() {
      if (this.fileRecords.length > 0 && !this.fileRecords[0].error) {
        return this.fileRecords[0];
      }
      return null;
    },

    wasAttachmentDeleted() {
      return this.attachmentDeleted;
    },

    getDeletedAttachmentPath() {
      return this.deletedAttachmentPath;
    },

    clearFiles() {
      this.fileRecords = [];
      this.attachmentDeleted = false;
    },
  },
};
</script>

<style scoped>
.financing-section {
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-bottom: 12px;
  overflow: hidden;
}

.financing-section__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  background-color: #f8f9fa;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.financing-section__header:hover {
  background-color: #e9ecef;
}

.financing-section__header--open {
  background-color: #e9ecef;
  border-bottom: 1px solid #ddd;
}

.financing-section__title {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.financing-section__icon {
  font-size: 11px;
  color: #666;
}

.financing-section__body {
  padding: 16px;
  background-color: #fff;
}

.financing-section__attachments {
  margin-top: 16px;
}

.financing-section__attachments-label {
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 8px;
}

.financing-section__existing-attachment {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.financing-section__attachment-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #333;
  text-decoration: none;
  font-size: 13px;
  flex: 1;
}

.financing-section__attachment-link:hover {
  color: #000;
}

.financing-section__attachment-remove {
  color: red;
  font-size: 14px;
}

.financing-section__required {
  color: red;
  margin-left: 2px;
}
</style>
